import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, TextField, Button, Select, MenuItem, FormControl, InputLabel, Alert } from '@mui/material';
import { useChowaTheme } from '../config/chowaThemeContext';
import { getChowaSeries, getChowaUnits } from '../api_config';

// Google Fonts から BIZ UDPGothic を直接読み込み
const fontFaceStyle = `
  @font-face {
    font-family: 'BIZ UDPGothic';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/bizudpgothic/v14/hES36X5pHAIBjmS84VL0Bue83nU.ttf) format('truetype');
  }
`;

// スタイルを head に追加（一度だけ）
if (typeof document !== 'undefined' && !document.querySelector('#biz-udp-gothic-font-newunit')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'biz-udp-gothic-font-newunit';
  styleElement.textContent = fontFaceStyle;
  document.head.appendChild(styleElement);
}

const NewUnitForm = ({ onBack, onSave, initialData = null, isEditMode = false }) => {
  const { getCurrentTheme } = useChowaTheme();
  const currentTheme = getCurrentTheme();
  const [availableSeries, setAvailableSeries] = useState([]);
  const [existingUnits, setExistingUnits] = useState([]);
  const [duplicateError, setDuplicateError] = useState('');
  const [newUnit, setNewUnit] = useState({
    custom_data: '',
    model_series_id: '',
    model_type: '',
    unit_serial_number: '',
    exhaust_gas: '',
    device: ''
  });

  // 全角文字が含まれているかチェックする関数
  const hasFullWidthChars = (str) => {
    if (!str) return false;
    return /[Ａ-Ｚａ-ｚ０-９]/.test(str);
  };

  // デバイスIDの重複チェック
  const checkDeviceDuplicate = (deviceId) => {
    if (!deviceId) return false;
    
    const duplicate = existingUnits.find(unit => 
      unit.device === deviceId && unit.id !== initialData?.id
    );
    
    if (duplicate) {
      setDuplicateError(`デバイスID "${deviceId}" は既に使用されています（ユニット: ${duplicate.custom_data}）`);
      return true;
    } else {
      setDuplicateError('');
      return false;
    }
  };

  // フォームのバリデーション（必須フィールド + 全角文字チェック + 重複チェック）
  const isFormValid = () => {
    const hasRequiredFields = newUnit.custom_data.trim() !== '' && 
           newUnit.exhaust_gas.trim() !== '' && 
           newUnit.model_series_id !== '' &&
           newUnit.model_type.trim() !== '';
    
    const hasValidChars = !hasFullWidthChars(newUnit.model_type) &&
           !hasFullWidthChars(newUnit.unit_serial_number) &&
           !hasFullWidthChars(newUnit.device);
    
    const hasNoDuplicate = !duplicateError;
    
    return hasRequiredFields && hasValidChars && hasNoDuplicate;
  };

  useEffect(() => {
    fetchAvailableSeries();
    fetchExistingUnits();
    if (initialData) {
      setNewUnit({
        custom_data: initialData.custom_data || '',
        model_series_id: initialData.model_series_id || '',
        model_type: initialData.model_type || '',
        unit_serial_number: initialData.unit_serial_number || '',
        exhaust_gas: initialData.exhaust_gas || '',
        device: initialData.device || ''
      });
    }
  }, [initialData]);

  const fetchAvailableSeries = async () => {
    try {
      const response = await getChowaSeries();
      if (response.data.status === 'success') {
        setAvailableSeries(response.data.data.series);
      } else {
        console.error('シリーズ取得エラー:', response.data.message);
        setAvailableSeries([]);
      }
    } catch (error) {
      console.error('機種シリーズの取得に失敗しました:', error);
      setAvailableSeries([]);
    }
  };

  const fetchExistingUnits = async () => {
    try {
      const response = await getChowaUnits();
      if (response.data.status === 'success') {
        setExistingUnits(response.data.data.units || []);
      }
    } catch (error) {
      console.error('既存ユニットの取得に失敗しました:', error);
      setExistingUnits([]);
    }
  };

  const handleInputChange = (field, value) => {
    setNewUnit(prev => ({
      ...prev,
      [field]: value
    }));
    
    // デバイスIDが変更された場合は重複チェック
    if (field === 'device') {
      checkDeviceDuplicate(value);
    }
  };

  const handleSave = () => {
    // 保存前に最終重複チェック
    if (newUnit.device && checkDeviceDuplicate(newUnit.device)) {
      return;
    }
    
    onSave(newUnit);
    setNewUnit({
      custom_data: '',
      model_series_id: '',
      model_type: '',
      unit_serial_number: '',
      exhaust_gas: '',
      device: ''
    });
    setDuplicateError('');
  };

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isEditMode ? 'ユニット編集' : '新規ユニット登録'}
        </Typography>
        <Button 
          variant="outlined" 
          onClick={onBack}
          sx={{ color: currentTheme.primary, borderColor: currentTheme.primary }}
        >
          一覧に戻る
        </Button>
      </Box>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Custom Data *"
            value={newUnit.custom_data}
            onChange={(e) => handleInputChange('custom_data', e.target.value)}
            required
            error={newUnit.custom_data.trim() === ''}
            helperText={newUnit.custom_data.trim() === '' ? "この項目は必須です" : "ユニットを識別するための一意のデータ"}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required error={newUnit.model_series_id === ''}>
            <InputLabel>機種シリーズ *</InputLabel>
            <Select
              value={newUnit.model_series_id}
              label="機種シリーズ *"
              onChange={(e) => handleInputChange('model_series_id', e.target.value)}
            >
              <MenuItem value="">選択してください</MenuItem>
              {availableSeries.map((series) => (
                <MenuItem key={series.id} value={series.id}>
                  {series.name}
                </MenuItem>
              ))}
            </Select>
            {newUnit.model_series_id === '' && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                この項目は必須です
              </Typography>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="機種号機 *"
            value={newUnit.model_type}
            onChange={(e) => handleInputChange('model_type', e.target.value)}
            placeholder="例: HV-300#1"
            error={hasFullWidthChars(newUnit.model_type) || newUnit.model_type.trim() === ''}
            helperText={
              hasFullWidthChars(newUnit.model_type) 
                ? "半角文字のみ入力してください" 
                : newUnit.model_type.trim() === ''
                  ? "この項目は必須です"
                  : "半角文字のみ入力可能"
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="ユニット製造番号"
            value={newUnit.unit_serial_number}
            onChange={(e) => handleInputChange('unit_serial_number', e.target.value)}
            placeholder="例: HV30U-01001"
            error={hasFullWidthChars(newUnit.unit_serial_number)}
            helperText={hasFullWidthChars(newUnit.unit_serial_number) ? "半角文字のみ入力してください" : "半角文字のみ入力可能"}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required error={newUnit.exhaust_gas.trim() === ''}>
            <InputLabel>排ガス規制 *</InputLabel>
            <Select
              value={newUnit.exhaust_gas}
              label="排ガス規制 *"
              renderValue={(value) => value ? <span style={{ fontFamily: '"BIZ UDPGothic", "Noto Sans JP", sans-serif', fontFeatureSettings: '"jp90" 1' }}>{value}</span> : ""}
              onChange={(e) => handleInputChange('exhaust_gas', e.target.value)}
            >
              <MenuItem value="">選択してください</MenuItem>
              <MenuItem value="Ⅰ" sx={{ fontFamily: '"BIZ UDPGothic", "Noto Sans JP", sans-serif', fontFeatureSettings: '"jp90" 1' }}>Ⅰ</MenuItem>
              <MenuItem value="Ⅱ" sx={{ fontFamily: '"BIZ UDPGothic", "Noto Sans JP", sans-serif', fontFeatureSettings: '"jp90" 1' }}>Ⅱ</MenuItem>
              <MenuItem value="Ⅲ" sx={{ fontFamily: '"BIZ UDPGothic", "Noto Sans JP", sans-serif', fontFeatureSettings: '"jp90" 1' }}>Ⅲ</MenuItem>
            </Select>
            {newUnit.exhaust_gas.trim() === '' && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                この項目は必須です
              </Typography>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="デバイスID"
            value={newUnit.device}
            onChange={(e) => handleInputChange('device', e.target.value)}
            placeholder="SigfoxデバイスID"
            error={hasFullWidthChars(newUnit.device) || !!duplicateError}
            helperText={
              hasFullWidthChars(newUnit.device) 
                ? "半角文字のみ入力してください" 
                : duplicateError 
                  ? duplicateError 
                  : "半角文字のみ入力可能"
            }
          />
        </Grid>
        {duplicateError && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {duplicateError}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              onClick={onBack}
              sx={{ color: currentTheme.primary, borderColor: currentTheme.primary }}
            >
              キャンセル
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSave}
              disabled={!isFormValid()}
              sx={{ backgroundColor: currentTheme.primary }}
            >
              保存
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default NewUnitForm;
